// ProductionHistoryMobile.jsx — onglet "Historique des productions" (mobile)
// À copier dans : src/GestionProductions/ProductionHistoryMobile.jsx
// Endpoint : getProductionHistory(page, limit, { warehouse_id, company_id }).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  SearchOutlined, BellOutlined, CloseOutlined, InboxOutlined, LoadingOutlined,
  ClockCircleOutlined, CalendarOutlined,
} from "@ant-design/icons";
import {
  getProductionDetails,
  getProductionHistory,
} from "./api";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import { getSelectedProductionTerminology } from "./productionTerminology";
import MobileModuleHeader, { MobileHeaderAction } from "../mobile/MobileModuleHeader";
import "./production-mobile-kit.css";
import ProductionExpenseRegularizationModal from "./ProductionExpenseRegularizationModal";

const PAGE = 20;
const fmtNum = (v, d = 0) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: d }).format(Number(v) || 0);
const fmtF = (v) => fmtNum(Math.round(Number(v) || 0)) + " F";

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
function dayLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Date inconnue";
  const now = new Date(); const yest = new Date(); yest.setDate(now.getDate() - 1);
  if (sameDay(d, now)) return "Aujourd'hui";
  if (sameDay(d, yest)) return "Hier";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}
const timeOf = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); };

function statusInfo(log) {
  if (log.__offline) return { cls: "pm-pill--amber", label: "En attente sync" };
  const s = String(log.status || "").toLowerCase();
  if (s === "failed") return { cls: "pm-pill--red", label: "Échouée" };
  if (s === "pending") return { cls: "pm-pill--amber", label: "À vérifier" };
  if (s === "deleted" || s === "cancelled") return { cls: "pm-pill--red", label: "Annulée" };
  return { cls: "pm-pill--green", label: "Terminée" };
}

export default function ProductionHistoryMobile({ onBack }) {
  const navigate = useNavigate();
  const outletContext = useOutletContext();
  const { user, hasPermission } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const t = getSelectedProductionTerminology(user, selectedCompany);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [regularizationCost, setRegularizationCost] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const canViewDetails = hasPermission(
    "Gestion Commerciale.Approvisionnement.Production.Historique.details"
  );
  const canRegularize = hasPermission(
    "Gestion Commerciale.Depenses.SaisieDepenses.create"
  );
  const canPay =
    hasPermission("Gestion Commerciale.Depenses.SaisieDepenses.view") &&
    hasPermission(
      "Gestion Commerciale.Depenses.SaisieDepenses.create_payment"
    );

  const openDetail = async (log) => {
    setDetail(log);
    setDetailData(null);
    if (log?.__offline || !canViewDetails) return;
    setDetailLoading(true);
    try {
      const data = await getProductionDetails(log.id, {
        warehouse_id: selectedWarehouse,
        company_id: selectedCompany,
      });
      setDetailData(data);
    } catch (error) {
      message.error(
        error?.error ||
          error?.message ||
          "Impossible de charger les détails de la production"
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setDetailData(null);
    setRegularizationCost(null);
  };

  const openRegularization = (cost) => {
    if (canRegularize) setRegularizationCost(cost);
  };
  const refreshAfterRegularization = async () => {
    const refreshed = await getProductionDetails(detailData.production.id, {
      warehouse_id: selectedWarehouse,
      company_id: selectedCompany,
    });
    setDetailData(refreshed);
  };

  // Recherche sur les productions déjà chargées (comme les puces de filtre) :
  // l'historique est paginé côté serveur, la saisie ne porte pas sur les pages
  // non encore récupérées.
  const toggleSearch = () => {
    if (searchOpen) { setSearchOpen(false); setQuery(""); return; }
    setSearchOpen(true);
    requestAnimationFrame(() => searchRef.current?.focus());
  };
  const goNotifications = () => navigate("/notifications");

  const fetchPage = (p) => {
    if (!selectedWarehouse) { setLogs([]); setTotal(0); return; }
    setLoading(true);
    getProductionHistory(p, PAGE, { warehouse_id: selectedWarehouse, company_id: selectedCompany })
      .then((data) => {
        const incoming = data?.logs || [];
        setLogs((prev) => (p === 1 ? incoming : [...prev, ...incoming]));
        setTotal(Number(data?.total || incoming.length));
      })
      .catch(() => message.error("Erreur lors du chargement de l'historique"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { setPage(1); fetchPage(1); }, [selectedWarehouse, selectedCompany]); // eslint-disable-line

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return logs.filter((l) => {
      if (q) {
        const haystack = `${l.production_unit_name || ""} ${l.production_code || ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (filter === "done") return !l.__offline && ["completed", "done", ""].includes(String(l.status || "").toLowerCase());
      if (filter === "pending") return l.__offline || ["pending", "failed"].includes(String(l.status || "").toLowerCase());
      return true;
    });
  }, [logs, filter, query]);

  const totalValue = useMemo(() => logs.reduce((s, l) => s + (Number(l.total_cost) || 0), 0), [logs]);
  const pendingCount = useMemo(() => logs.filter((l) => l.__offline || String(l.status || "").toLowerCase() === "pending").length, [logs]);

  // regroupement par jour
  const groups = useMemo(() => {
    const out = []; let cur = null;
    for (const l of visible) {
      const lbl = dayLabel(l.created_at);
      if (!cur || cur.label !== lbl) { cur = { label: lbl, items: [] }; out.push(cur); }
      cur.items.push(l);
    }
    return out;
  }, [visible]);

  if (!selectedWarehouse) {
    return (
      <div className="pm-root"><Header t={t} onBack={onBack} onMenu={outletContext?.openMobileMenu} onBell={goNotifications} />
        <div className="pm-body"><div className="pm-state">
          <div className="pm-state-badge"><InboxOutlined /></div>
          <h3 className="pm-state-title">Aucun établissement sélectionné</h3>
          <p className="pm-state-text">Choisissez un établissement pour voir son historique.</p>
        </div></div>
      </div>
    );
  }

  return (
    <div className="pm-root">
      <Header t={t} onBack={onBack} onMenu={outletContext?.openMobileMenu} onSearch={toggleSearch} searchActive={searchOpen} onBell={goNotifications} />
      <div className="pm-body">
        <div className="pm-summary">
          {[{ v: fmtNum(total), l: "Productions" }, { v: fmtF(totalValue).replace(" F", ""), l: "Valeur (F)" }, { v: fmtNum(pendingCount), l: "À vérifier" }].map((it, i) => (
            <React.Fragment key={it.l}>
              {i > 0 && <div className="pm-summary-div" />}
              <div className="pm-summary-cell"><span className="pm-summary-val">{it.v}</span><span className="pm-summary-lbl">{it.l}</span></div>
            </React.Fragment>
          ))}
        </div>

        {searchOpen && (
          <div className="pm-search" style={{ marginBottom: 12 }}>
            <SearchOutlined />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un lot ou une production…"
            />
            <button className="pm-iconbtn" style={{ width: 28, height: 28 }} onClick={toggleSearch}>
              <CloseOutlined />
            </button>
          </div>
        )}

        <div className="pm-chips">
          {[{ k: "all", l: "Toutes" }, { k: "done", l: "Terminées" }, { k: "pending", l: "À vérifier" }].map((f) => (
            <button key={f.k} className={`pm-chip-f ${filter === f.k ? "is-active" : ""}`}
              style={filter === f.k ? { color: "var(--pm-card)" } : undefined} onClick={() => setFilter(f.k)}>{f.l}</button>
          ))}
        </div>

        {loading && logs.length === 0 ? (
          <div className="pm-spin"><LoadingOutlined /></div>
        ) : visible.length === 0 ? (
          query.trim() ? (
            <div className="pm-state"><div className="pm-state-badge"><SearchOutlined /></div>
              <h3 className="pm-state-title">Aucun résultat</h3>
              <p className="pm-state-text">Aucune production chargée ne correspond à « {query.trim()} ».</p></div>
          ) : (
            <div className="pm-state"><div className="pm-state-badge"><CalendarOutlined /></div>
              <h3 className="pm-state-title">Aucune production</h3>
              <p className="pm-state-text">Les productions lancées apparaîtront ici avec leurs coûts réels.</p></div>
          )
        ) : (
          <>
            {groups.map((g) => (
              <div key={g.label}>
                <div className="pm-dayhead">{g.label}</div>
                {g.items.map((l, i) => {
                  return (
                    <button key={l.production_code || l.id || i} className="pm-lotcard" onClick={() => openDetail(l)}>
                      <div className="pm-iconchip" style={{ width: 44, height: 44, borderRadius: 13, fontSize: 20,
                        background: l.__offline ? "var(--pm-amber-bg)" : "var(--pm-primary-soft)", color: l.__offline ? "var(--pm-amber)" : "var(--pm-primary)" }}>
                        {l.__offline ? <ClockCircleOutlined /> : <InboxOutlined />}
                      </div>
                      <div className="pm-lotcard-mid">
                        <div className="pm-lotcard-top"><span className="pm-lotcard-name">{l.production_unit_name}</span></div>
                        <div className="pm-lotcard-meta">
                          <span className="pm-lot">{l.production_code}</span>
                          {l.production_batch_id && (
                            <>
                              <span>·</span>
                              <span>
                                Lot groupe #{l.production_batch_id} (
                                {l.production_batch_sequence}/
                                {l.production_batch_item_count})
                              </span>
                            </>
                          )}
                          <span>·</span><span>{fmtNum(l.output_quantity, 2)} {l.unit_short_name || ""}</span>
                          {timeOf(l.created_at) && <><span>·</span><span>{timeOf(l.created_at)}</span></>}
                        </div>
                      </div>
                      <div className="pm-lotcard-right">
                        <div className="pm-lotcard-cost">{fmtF(l.total_cost)}</div>
                        <div className="pm-lotcard-unit">{fmtF(l.unit_cost_real)} / u</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
            {logs.length < total && (
              <button className="pm-btn pm-btn--ghost pm-btn--block" style={{ margin: "6px 0 4px" }}
                disabled={loading} onClick={() => { const np = page + 1; setPage(np); fetchPage(np); }}>
                {loading ? <LoadingOutlined /> : null}Charger plus ({logs.length}/{total})
              </button>
            )}
          </>
        )}
        <div style={{ height: 24 }} />
      </div>

      {/* feuille détail */}
      {detail && (
        <>
          <div className="pm-scrim" onClick={closeDetail} />
          <div className="pm-sheet">
            <div className="pm-grab" />
            <div className="pm-sheet-head">
              <div>
                <h2 className="pm-sheet-title" style={{ fontSize: 18 }}>{detail.production_unit_name}</h2>
                <p className="pm-sheet-sub">{dayLabel(detail.created_at)} · {timeOf(detail.created_at)}</p>
              </div>
              <button className="pm-iconbtn" onClick={closeDetail}><CloseOutlined /></button>
            </div>
            <div className="pm-detail-grid">
              <div className="pm-detail-row"><span className="k">Lot</span><span className="v"><span className="pm-lot" style={{ fontSize: 12.5, padding: "4px 10px" }}>{detail.production_code}</span></span></div>
              {detail.production_batch_id && (
                <div className="pm-detail-row">
                  <span className="k">Lot groupe</span>
                  <span className="v">
                    #{detail.production_batch_id} · {detail.production_batch_sequence}/
                    {detail.production_batch_item_count}
                  </span>
                </div>
              )}
              <div className="pm-detail-row"><span className="k">Quantité produite</span><span className="v">{fmtNum(detail.output_quantity, 2)} {detail.unit_short_name || ""}</span></div>
              <div className="pm-detail-row"><span className="k">Coût des {t.materialPlural}</span><span className="v">{fmtF(detail.materials_cost_total)}</span></div>
              <div className="pm-detail-row"><span className="k">Coûts variables</span><span className="v">{fmtF(detail.variable_cost_total)}</span></div>
              <div className="pm-detail-row"><span className="k">Coût total</span><span className="v">{fmtF(detail.total_cost)}</span></div>
              <div className="pm-detail-row"><span className="k">Coût unitaire réel</span><span className="v">{fmtF(detail.unit_cost_real)}</span></div>
              <div className="pm-detail-row"><span className="k">Réalisée par</span><span className="v">{detail.user_name || "Système"}</span></div>
              <div className="pm-detail-row"><span className="k">Statut</span><span className="v"><span className={`pm-pill pm-pill--sm ${statusInfo(detail).cls}`}>{statusInfo(detail).label}</span></span></div>
            </div>
            {detailLoading ? (
              <div className="pm-spin"><LoadingOutlined /></div>
            ) : detailData?.variable_costs?.length ? (
              <div style={{ marginTop: 18 }}>
                <h3 className="pm-sheet-title" style={{ fontSize: 16 }}>
                  Charges variables et paiements
                </h3>
                {Number(
                  detailData.variable_cost_financial_summary
                    ?.pending_regularization_count || 0
                ) > 0 ? (
                  <div className="pm-callout pm-callout--amber" style={{ margin: "10px 0" }}>
                    <div className="pm-callout-sub">
                      Le coût de revient est constaté, mais certaines dépenses
                      restent à régulariser.
                    </div>
                  </div>
                ) : null}
                <div className="pm-list">
                  {detailData.variable_costs.map((cost) => {
                    const pending =
                      cost.financial_status !== "expense_created" &&
                      cost.financial_status !== "cancelled" &&
                      cost.financial_status !== "not_applicable";
                    return (
                      <div className="pm-card" key={cost.id} style={{ marginBottom: 10 }}>
                        <div className="pm-detail-row">
                          <span className="k">{cost.label}</span>
                          <span className="v">{fmtF(cost.total_cost)}</span>
                        </div>
                        <div className="pm-detail-row">
                          <span className="k">Fournisseur</span>
                          <span className="v">{cost.supplier_name || "À compléter"}</span>
                        </div>
                        <div className="pm-detail-row">
                          <span className="k">Catégorie</span>
                          <span className="v">{cost.expense_category_name || "À compléter"}</span>
                        </div>
                        <div className="pm-detail-row">
                          <span className="k">Suivi financier</span>
                          <span className="v">
                            <span className={`pm-pill pm-pill--sm ${pending ? "pm-pill--amber" : cost.payment_status === "Payé" ? "pm-pill--green" : "pm-pill--blue"}`}>
                              {pending
                                ? "À régulariser"
                                : cost.payment_status ||
                                  (cost.financial_status === "cancelled"
                                    ? "Annulée"
                                    : "Sans montant")}
                            </span>
                          </span>
                        </div>
                        {pending && cost.can_regularize && canRegularize ? (
                          <button
                            className="pm-btn pm-btn--primary pm-btn--block"
                            onClick={() => openRegularization(cost)}
                          >
                            Régulariser en dépense
                          </button>
                        ) : null}
                        {cost.expense_id &&
                        Number(cost.due_amount || 0) > 0 &&
                        canPay ? (
                          <button
                            className="pm-btn pm-btn--ghost pm-btn--block"
                            onClick={() => {
                              closeDetail();
                              navigate(
                                `/gestion-charges-admin/saisie?expense_id=${cost.expense_id}&action=pay`
                              );
                            }}
                          >
                            Payer la dépense
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </>
      )}

      {regularizationCost && detailData && (
        <ProductionExpenseRegularizationModal
          key={regularizationCost.id}
          cost={regularizationCost}
          details={detailData}
          onClose={() => setRegularizationCost(null)}
          onSuccess={refreshAfterRegularization}
        />
      )}
    </div>
  );
}

function Header({ t, onBack, onMenu, onSearch, searchActive, onBell }) {
  return (
    <MobileModuleHeader
      title={t.historyTitle}
      subtitle={`Toutes vos ${t.costContext === "cuisine" ? "préparations" : "productions"} et leurs coûts réels`}
      onMenu={onMenu}
      onLeading={onMenu ? undefined : onBack}
      leadingIcon="arrow-left"
      leadingLabel="Retour"
      actions={
        <>
          {onSearch ? (
            <MobileHeaderAction
              label="Rechercher une production"
              onClick={onSearch}
              active={Boolean(searchActive)}
            >
              <SearchOutlined />
            </MobileHeaderAction>
          ) : null}
          <MobileHeaderAction label="Notifications" onClick={onBell}>
            <BellOutlined />
          </MobileHeaderAction>
        </>
      }
    />
  );
}
